import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  X,
  Clock,
  MapPin,
  Tag,
  CheckCircle2,
  Calendar,
  Sparkles,
  ArrowRight,
  ShieldCheck,
  Brain,
  Feather
} from 'lucide-react';

// Images mapped with Cloudflare CDN resizing parameters
const IMAGES = {
  pladsTilUro: "https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/plads-til-uro-i-luften.png",
  foerAfgangForberedelsen: "https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/foer-afgang-forberedelsen.png",
  afgang: "https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/afgang.png",
  sikkerhedRoligeSkridt: "https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/sikkerhed-rolige-skridt.png",
  boardingOmbord: "https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/boarding-ombord.png",
  larsPortraet: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/lars-nissen-corell-portraet-png1448x1086-1.png",
  // Carousel slides
  carousel: [
    {
      id: 1,
      image: "https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/foer-rejsen.png",
      overtitle: "01 · Før rejsen",
      title: "Bekymringen kan begynde hjemme",
      text: "Tankerne om flyveturen kan begynde længe før afgang. Måske tjekker du vejret, søger efter beroligelse eller overvejer, om du skal aflyse. Jo mere tid og opmærksomhed bekymringen får, desto mere virkelig kan faren begynde at føles.",
      alt: "Før Rejsen – illustration af tanker og forberedelse før afgang"
    },
    {
      id: 2,
      image: "https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/ved-gaten.png",
      overtitle: "02 · Ved gaten",
      title: "Ventetiden kan sætte opmærksomheden på overarbejde",
      text: "Ved gaten kan opmærksomheden blive fanget af afgangstavlen, vejret, flyet og de andre passagerer. Du leder måske efter tegn på, om noget er galt. Det kan få uroen til at vokse, selvom der ikke er kommet nye oplysninger.",
      alt: "Ved Gaten – illustration af ventetid og opmærksomhed i lufthavnen"
    },
    {
      id: 3,
      image: "https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/start.png",
      overtitle: "03 · Start",
      title: "Kroppen kan larme, uden at du behøver overvåge alt",
      text: "Under starten kan lyde, acceleration og kropslige fornemmelser føles meget tydelige. Uroen er ikke i sig selv et tegn på fare. Du kan registrere det, du mærker, uden hele tiden at undersøge, om noget er galt.",
      alt: "Start – illustration af flyets afgang og kropslig uro"
    },
    {
      id: 4,
      image: "https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/turbulens.png",
      overtitle: "04 · Turbulens",
      title: "Opmærksomheden kan trænes til at blive mere fleksibel",
      text: "Målet er ikke at ignorere turbulensen eller presse dig selv til at være rolig. Du kan mærke uro og samtidig vælge, hvad du vil give din opmærksomhed. Turbulensen må gerne være der, uden at den får hele scenen.",
      alt: "Turbulens – illustration af fleksibel opmærksomhed under flyvning"
    },
    {
      id: 5,
      image: "https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/efter-rejsen.png",
      overtitle: "05 · Efter rejsen",
      title: "Målet er større frihed – ikke en perfekt flyvetur",
      text: "Efter rejsen kan du få lyst til at gennemgå alle lyde, bevægelser og kropslige reaktioner. Men oplevelsen behøver ikke analyseres til bunds. Rejsen må godt være slut, også selvom den ikke føltes perfekt.",
      alt: "Efter Rejsen – illustration af at lade rejsen slutte i ro"
    }
  ]
};

// FAQ Data for Infographic FAQ
const FLIGHT_FAQ_ITEMS = [
  {
    question: "Kan flyskræk behandles?",
    answerBadge: {
      smallText: "Kort svar",
      bigText: "Ja"
    },
    cards: [
      {
        title: "Flyskrækken kan ændre sig",
        text: "Du behøver ikke være låst fast i samme mønster som nu.",
        icon: (
          <svg className="w-6 h-6 text-[#123F13]" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
          </svg>
        )
      },
      {
        title: "Vi arbejder med mønsteret",
        text: "Fokus er på tanker, kropslig alarm, bekymring og de strategier, der holder angsten i gang.",
        icon: (
          <svg className="w-6 h-6 text-[#123F13]" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm0 6h9.75M10.5 12a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm0 6h9.75M10.5 18a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM3.75 6H6m-2.25 6H6m-2.25 6H6" />
          </svg>
        )
      },
      {
        title: "Målet er mere frihed",
        text: "Det handler om at kunne flyve med mindre kamp og mere valgfrihed.",
        icon: (
          <svg className="w-6 h-6 text-[#123F13]" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75l3 3m0 0l3-3m-3 3v-7.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      }
    ],
    summary: "Flyskræk kan behandles. Målet er ikke nødvendigvis total ro hele tiden, men at angsten fylder mindre og styrer mindre.",
    links: [
      { label: "Book første session", path: "/bestil-tid/" },
      { label: "Læs om angst", path: "/angst/" }
    ]
  },
  {
    question: "Hvad hvis jeg ved, at flyvning er sikkert, men stadig bliver bange?",
    answerBadge: {
      smallText: "Kort svar",
      bigText: "Det er meget almindeligt"
    },
    cards: [
      {
        title: "Viden er ikke altid nok",
        text: "Man kan godt vide, at noget er sikkert og stadig reagere med stærk uro.",
        icon: (
          <svg className="w-6 h-6 text-[#123F13]" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 2.625a3.375 3.375 0 00-3.75-3.375m-3.75 3.375a3.375 3.375 0 013.75-3.375m0 0V5.25m0 0A2.25 2.25 0 009.75 3h-1.5A2.25 2.25 0 006 5.25v2.25m6-2.25A2.25 2.25 0 0114.25 3h1.5A2.25 2.25 0 0118 5.25v2.25" />
          </svg>
        )
      },
      {
        title: "Kroppen reagerer hurtigt",
        text: "Angst kan være en automatisk alarmreaktion og ikke et tegn på manglende logik.",
        icon: (
          <svg className="w-6 h-6 text-[#123F13]" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
          </svg>
        )
      },
      {
        title: "Vi arbejder med reaktionen",
        text: "Behandlingen handler om, hvordan du forholder dig til uro, tanker og kropslige signaler.",
        icon: (
          <svg className="w-6 h-6 text-[#123F13]" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
          </svg>
        )
      }
    ],
    summary: "Mange med flyskræk ved godt, at fly er sikre. Problemet er ofte, at kroppen og opmærksomheden reagerer, som om der er fare alligevel.",
    links: [
      { label: "Metakognitiv terapi", path: "/metakognitiv-terapi/" },
      { label: "Flyskræk", path: "/flyskraek/" }
    ]
  },
  {
    question: "Hvad hvis jeg er mest bange for at få et panikanfald i flyet?",
    answerBadge: {
      smallText: "Kort svar",
      bigText: "Det kan der arbejdes med"
    },
    cards: [
      {
        title: "Frygten for panik kan blive selve problemet",
        text: "Mange er mere bange for panikken end for flyet.",
        icon: (
          <svg className="w-6 h-6 text-[#123F13]" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
        )
      },
      {
        title: "Kroppen kan larme højt",
        text: "Hjertebanken, svimmelhed og uro kan føles voldsomme uden at være farlige.",
        icon: (
          <svg className="w-6 h-6 text-[#123F13]" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
          </svg>
        )
      },
      {
        title: "Mindre overvågning hjælper ofte",
        text: "Når du lærer at give symptomerne mindre opmærksomhed, fylder de ofte mindre.",
        icon: (
          <svg className="w-6 h-6 text-[#123F13]" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        )
      }
    ],
    summary: "Hvis du især frygter et panikanfald, arbejder vi med forholdet til kropslige signaler og frygten for at miste kontrollen.",
    links: [
      { label: "Læs om panikangst", path: "/panikangst/" },
      { label: "Angst", path: "/angst/" }
    ]
  },
  {
    question: "Skal jeg lære at tænke positivt om flyvning?",
    answerBadge: {
      smallText: "Kort svar",
      bigText: "Nej, ikke nødvendigvis"
    },
    cards: [
      {
        title: "Målet er ikke pæne tanker",
        text: "Du behøver ikke overbevise dig selv om, at alt føles godt.",
        icon: (
          <svg className="w-6 h-6 text-[#123F13]" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
          </svg>
        )
      },
      {
        title: "Mere fleksibilitet er vigtigere",
        text: "Det vigtige er, at tankerne får mindre magt over dig.",
        icon: (
          <svg className="w-6 h-6 text-[#123F13]" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m-3-12L21 9m0 0l-4.5 4.5M21 9H7.5" />
          </svg>
        )
      },
      {
        title: "Mindre kamp, mere retning",
        text: "Du kan lære at lade tanker være der uden at følge dem hele vejen.",
        icon: (
          <svg className="w-6 h-6 text-[#123F13]" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
          </svg>
        )
      }
    ],
    summary: "Du behøver ikke træne dig til at tænke positivt. Det vigtigste er ofte at bruge mindre tid på kamp, analyse og beroligelse.",
    links: [
      { label: "Metakognitiv terapi", path: "/metakognitiv-terapi/" },
      { label: "Tankemylder", path: "/tankemylder/" }
    ]
  },
  {
    question: "Kan hypnose hjælpe ved flyskræk?",
    answerBadge: {
      smallText: "Kort svar",
      bigText: "Ja, for nogle"
    },
    cards: [
      {
        title: "Hypnose er en mulighed",
        text: "Den kan bruges som støtte, hvis det giver mening for dig.",
        icon: (
          <svg className="w-6 h-6 text-[#123F13]" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
          </svg>
        )
      },
      {
        title: "Ro og fokuseret opmærksomhed",
        text: "Hypnose bruges jordnært som rolig guidning og fokuseret opmærksomhed.",
        icon: (
          <svg className="w-6 h-6 text-[#123F13]" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
          </svg>
        )
      },
      {
        title: "Ikke et krav",
        text: "Du kan også få hjælp helt uden hypnose. Den metakognitive forståelse er central i arbejdet.",
        icon: (
          <svg className="w-6 h-6 text-[#123F13]" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      }
    ],
    summary: "Hypnose kan være hjælpsom ved flyskræk, men det er en mulighed og ikke et krav. Det vælges kun, hvis det passer til dig.",
    links: [
      { label: "Læs om hypnose", path: "/hypnose/" },
      { label: "Book første session", path: "/bestil-tid/" }
    ]
  },
  {
    question: "Hvad kan jeg gøre, når der kommer turbulens?",
    answerBadge: {
      smallText: "Kort svar",
      bigText: "Hold retningen enkel"
    },
    cards: [
      {
        title: "Gør det praktiske først",
        text: "Sæt dig godt, spænd bæltet og følg besætningens instruktioner.",
        icon: (
          <svg className="w-6 h-6 text-[#123F13]" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751A11.959 11.959 0 0112 2.714z" />
          </svg>
        )
      },
      {
        title: "Lad ikke uroen tage styringen",
        text: "Turbulens kan være ubehagelig, men er ikke i sig selv tegn på, at noget går galt.",
        icon: (
          <svg className="w-6 h-6 text-[#123F13]" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
          </svg>
        )
      },
      {
        title: "Vend tilbage til kroppen og rummet",
        text: "Ret opmærksomheden mod sædet, gulvet, vejrtrækningen og det, du konkret kan registrere.",
        icon: (
          <svg className="w-6 h-6 text-[#123F13]" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
          </svg>
        )
      }
    ],
    summary: "Når turbulens kommer, hjælper det ofte at holde fokus enkelt, lade være med at overanalysere og vende opmærksomheden tilbage til det konkrete.",
    links: [
      { label: "Angst", path: "/angst/" },
      { label: "Book første session", path: "/bestil-tid/" }
    ]
  },
  {
    question: "Hvorfor begynder min bekymring længe før flyrejsen?",
    answerBadge: {
      smallText: "Kort svar",
      bigText: "Fordi hjernen prøver at forebygge fare"
    },
    cards: [
      {
        title: "Bekymringen starter tit tidligt",
        text: "For nogle begynder den dage eller uger før rejsen.",
        icon: (
          <svg className="w-6 h-6 text-[#123F13]" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
          </svg>
        )
      },
      {
        title: "Det kan blive en mental rundkørsel",
        text: "Tjek, forestillinger, googling og gennemgang kan holde uroen varm.",
        icon: (
          <svg className="w-6 h-6 text-[#123F13]" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
          </svg>
        )
      },
      {
        title: "Vi arbejder også før flyveturen",
        text: "En vigtig del er at ændre forholdet til bekymring og forberedende kontrol.",
        icon: (
          <svg className="w-6 h-6 text-[#123F13]" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25a2.251 2.251 0 012.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C7.533 4.022 6.75 4.887 6.75 5.922v11.917c0 1.035.783 1.9 1.976 2.007 1.13.103 2.277.156 3.434.156 1.157 0 2.305-.053 3.434-.156 1.193-.107 1.976-.972 1.976-2.007V5.922c0-1.035-.783-1.9-1.976-2.007a44.82 44.82 0 00-1.124-.08" />
          </svg>
        )
      }
    ],
    summary: "Bekymringen før rejsen er ofte et forsøg på at skabe kontrol. Problemet er, at den forberedende bekymring selv kan holde angsten i live.",
    links: [
      { label: "Tankemylder", path: "/tankemylder/" },
      { label: "Stress", path: "/stress/" }
    ]
  },
  {
    question: "Kan behandlingen foregå online?",
    answerBadge: {
      smallText: "Kort svar",
      bigText: "Ja"
    },
    cards: [
      {
        title: "Online hjemmefra",
        text: "Du kan deltage fra et roligt sted med stabil internetforbindelse.",
        icon: (
          <svg className="w-6 h-6 text-[#123F13]" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125h5.25v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h5.25c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
          </svg>
        )
      },
      {
        title: "I klinikken i Valby",
        text: "Du kan også møde fysisk, hvis det passer dig bedre.",
        icon: (
          <svg className="w-6 h-6 text-[#123F13]" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
          </svg>
        )
      },
      {
        title: "Du vælger rammen",
        text: "Vi finder den form, der giver bedst mening for dig og din hverdag.",
        icon: (
          <svg className="w-6 h-6 text-[#123F13]" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75l3 3m0 0l3-3m-3 3v-7.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      }
    ],
    summary: "Behandlingen kan foregå både online og i klinikken i Valby. Du vælger den ramme, der passer bedst.",
    links: [
      { label: "Læs om online sessioner", path: "/online-sessioner/" },
      { label: "Book første session", path: "/bestil-tid/" }
    ]
  },
  {
    question: "Hvor mange sessioner har jeg brug for?",
    answerBadge: {
      smallText: "Kort svar",
      bigText: "Det varierer"
    },
    cards: [
      {
        title: "Det afhænger af dit mønster",
        text: "Hvor meget flyskrækken fylder, og hvad du gerne vil kunne, spiller ind.",
        icon: (
          <svg className="w-6 h-6 text-[#123F13]" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm0 6h9.75M10.5 12a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm0 6h9.75M10.5 18a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM3.75 6H6m-2.25 6H6m-2.25 6H6" />
          </svg>
        )
      },
      {
        title: "Første session skaber overblik",
        text: "Vi bruger den første session til at forstå din situation og retning.",
        icon: (
          <svg className="w-6 h-6 text-[#123F13]" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
          </svg>
        )
      },
      {
        title: "Du får en plan",
        text: "Efter første session får du en individuel behandlingsplan med næste skridt.",
        icon: (
          <svg className="w-6 h-6 text-[#123F13]" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      }
    ],
    summary: "Antallet af sessioner afhænger af dit mønster, dine mål og hvor fastlåst flyskrækken er blevet. Du får en individuel plan efter første session.",
    links: [
      { label: "Book første session", path: "/bestil-tid/" },
      { label: "Se priser", path: "/priser/" }
    ]
  }
];

// Custom Carousel Component
const Carousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const carouselRef = useRef(null);

  const minSwipeDistance = 50;

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % IMAGES.carousel.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + IMAGES.carousel.length) % IMAGES.carousel.length);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  // Touch handlers for mobile swipe
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowLeft') {
      prevSlide();
    } else if (e.key === 'ArrowRight') {
      nextSlide();
    }
  };

  const currentSlide = IMAGES.carousel[currentIndex];

  return (
    <div
      ref={carouselRef}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      aria-label="Billedkarussel med fem situationer på flyrejsen"
      className="outline-none focus-visible:ring-2 focus-visible:ring-[#123F13] rounded-2xl"
    >
      <div className="bg-white rounded-2xl border border-[#E8DDCE] p-4 sm:p-6 md:p-8 shadow-sm">
        {/* Active slide counter & indicators */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-[#E8DDCE]/60 pb-4 mb-6 gap-3">
          <span className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold tracking-wider text-[#8C2F12] uppercase bg-[#FDF6F0] px-3.5 py-1.5 rounded-full border border-[#8C2F12]/15 shrink-0 self-start sm:self-auto">
            {currentSlide.overtitle}
          </span>
          <span className="text-sm font-medium text-[#3B352F]/70">
            Slide {currentIndex + 1} af {IMAGES.carousel.length}
          </span>
        </div>

        {/* Carousel Navigation Controls */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pb-6 mb-6 border-b border-[#E8DDCE]/60">
          {/* Navigation Dots */}
          <div className="flex items-center gap-2.5" role="tablist" aria-label="Karussel navigation">
            {IMAGES.carousel.map((slide, index) => (
              <button
                key={slide.id}
                onClick={() => goToSlide(index)}
                role="tab"
                aria-selected={currentIndex === index}
                aria-label={`Gå til slide ${index + 1}: ${slide.title}`}
                className={`h-3 rounded-full transition-all duration-300 cursor-pointer ${
                  currentIndex === index
                    ? 'w-8 bg-[#123F13]'
                    : 'w-3 bg-[#E8DDCE] hover:bg-[#8C2F12]/40'
                }`}
              />
            ))}
          </div>

          {/* Prev/Next Buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={prevSlide}
              aria-label="Forrige slide"
              className="p-3 rounded-xl border border-[#E8DDCE] bg-[#FAF8F5] text-[#123F13] hover:bg-[#123F13] hover:text-white transition-all cursor-pointer shadow-2xs active:scale-95 flex items-center justify-center"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={nextSlide}
              aria-label="Næste slide"
              className="p-3 rounded-xl border border-[#E8DDCE] bg-[#FAF8F5] text-[#123F13] hover:bg-[#123F13] hover:text-white transition-all cursor-pointer shadow-2xs active:scale-95 flex items-center justify-center"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Slide Content */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-center">
          {/* Image Container - Fills frame naturally */}
          <div className="lg:col-span-7 bg-[#FAF8F5] rounded-xl border border-[#E8DDCE]/60 overflow-hidden p-2 sm:p-3 flex items-center justify-center">
            <img
              src={currentSlide.image}
              alt={currentSlide.alt}
              className="w-full h-auto block rounded-lg transition-all duration-300"
            />
          </div>

          {/* Text Content */}
          <div className="lg:col-span-5 space-y-4">
            <h3 className="text-2xl sm:text-3xl font-serif text-[#123F13] font-bold leading-snug">
              {currentSlide.title}
            </h3>
            <p className="text-[#3B352F]/80 text-base sm:text-lg leading-relaxed">
              {currentSlide.text}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};



export default function Flyskraek() {
  const canonicalUrl = "https://www.corehypnose.dk/flyskraek/";

  const schemaJsonLd = {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    "name": "Flyskræk – metakognitiv terapi og hypnose i Valby | Core Hypnose",
    "description": "Få hjælp til flyskræk med metakognitiv terapi og hypnose hos Core Hypnose i Valby eller online. Første behandlingssession koster 375 kr.",
    "url": canonicalUrl,
    "provider": {
      "@type": "LocalBusiness",
      "name": "Core Hypnose v/ Lars Nissen Corell – Psykoterapeut MPF",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Valby Langgade 142",
        "addressLocality": "Valby",
        "postalCode": "2500",
        "addressCountry": "DK"
      },
      "telephone": "+4527204671"
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] text-[#3B352F] antialiased">
      <Helmet>
        <title>Flyskræk – metakognitiv terapi og hypnose i Valby | Core Hypnose</title>
        <meta
          name="description"
          content="Få hjælp til flyskræk med metakognitiv terapi og hypnose hos Core Hypnose i Valby eller online. Første behandlingssession koster 375 kr."
        />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(schemaJsonLd)}</script>
      </Helmet>

      {/* 1. HERO OG INTRODUKTION */}
      <section className="pt-10 pb-16 md:pt-16 md:pb-24 bg-gradient-to-b from-[#F4EFE6] to-[#FAF8F5] border-b border-[#E8DDCE]/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            
            {/* Left Content */}
            <div className="lg:col-span-6 space-y-6">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#123F13]/10 text-[#123F13] text-xs sm:text-sm font-semibold tracking-wide border border-[#123F13]/15">
                <Feather className="w-4 h-4 text-[#123F13]" />
                <span>Hjælp til flyskræk</span>
              </div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-[#123F13] leading-[1.15] tracking-tight">
                Flyskræk behøver ikke styre din næste rejse
              </h1>

              <p className="text-lg sm:text-xl text-[#3B352F]/85 leading-relaxed font-normal">
                Du kan godt vide, at flyvning generelt er sikkert, og stadig mærke stærk uro, katastrofetanker eller frygt for at miste kontrollen. Jeg hjælper med flyskræk gennem metakognitiv terapi og, når det er relevant, hypnose.
              </p>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
                <Link
                  to="/bestil-tid"
                  className="inline-flex items-center justify-center gap-2 bg-[#123F13] text-white px-6 py-4 rounded-xl font-bold text-base hover:bg-[#123F13]/90 transition-all shadow-md hover:shadow-lg active:scale-[0.99] text-center"
                >
                  <Calendar className="w-5 h-5" />
                  <span>Book første behandlingssession</span>
                </Link>

                <a
                  href="#fem-situationer"
                  className="inline-flex items-center justify-center gap-2 bg-white text-[#123F13] border border-[#E8DDCE] px-6 py-4 rounded-xl font-semibold text-base hover:bg-[#F4EFE6] hover:border-[#123F13]/30 transition-all shadow-2xs text-center"
                >
                  <span>Se rejsens fem situationer</span>
                  <ArrowRight className="w-4 h-4 text-[#8C2F12]" />
                </a>
              </div>

              {/* Practical Info Badges */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-4 border-t border-[#E8DDCE]/70">
                <div className="bg-white/90 p-3.5 rounded-xl border border-[#E8DDCE] shadow-2xs text-center flex flex-col justify-center items-center h-full">
                  <div className="text-xs text-[#3B352F]/70 uppercase font-semibold tracking-wider">Pris</div>
                  <div className="text-lg font-bold text-[#123F13] mt-0.5">375 kr.</div>
                </div>

                <div className="bg-white/90 p-3.5 rounded-xl border border-[#E8DDCE] shadow-2xs text-center flex flex-col justify-center items-center h-full">
                  <div className="text-xs text-[#3B352F]/70 uppercase font-semibold tracking-wider">Varighed</div>
                  <div className="text-lg font-bold text-[#123F13] mt-0.5">50 minutter</div>
                </div>

                <div className="bg-white/90 p-3.5 rounded-xl border border-[#E8DDCE] shadow-2xs text-center flex flex-col justify-center items-center h-full">
                  <div className="text-xs text-[#3B352F]/70 uppercase font-semibold tracking-wider">Sted</div>
                  <div className="text-lg font-bold text-[#123F13] mt-0.5">Valby el. online</div>
                </div>

                <div className="bg-white/90 p-3.5 rounded-xl border border-[#E8DDCE] shadow-2xs text-center flex flex-col justify-center items-center h-full">
                  <div className="text-xs text-[#3B352F]/70 uppercase font-semibold tracking-wider">Vilkår</div>
                  <div className="text-sm font-bold text-[#8C2F12] mt-0.5">Kun for nye klienter</div>
                </div>
              </div>
            </div>

            {/* Right Hero Image */}
            <div className="lg:col-span-6 space-y-3">
              <div className="bg-white rounded-2xl border border-[#E8DDCE] shadow-md overflow-hidden p-2.5 sm:p-3 flex items-center justify-center">
                <img
                  src={IMAGES.pladsTilUro}
                  alt="Plads til uro i luften – grafisk illustration om flyskræk"
                  className="w-full h-auto block rounded-xl"
                />
              </div>
              <p className="text-sm text-center text-[#3B352F]/75 italic bg-white/60 py-1.5 px-4 rounded-lg border border-[#E8DDCE]/40">
                Plads til uro i luften – uden at uroen behøver styre hele rejsen.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* 2. HVAD ER FLYSKRÆK? */}
      <section className="py-16 md:py-24 border-b border-[#E8DDCE]/40 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            
            {/* Text & List */}
            <div className="lg:col-span-6 space-y-6">
              <div>
                <span className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold tracking-wider text-[#8C2F12] uppercase bg-[#FDF6F0] px-3.5 py-1.5 rounded-full border border-[#8C2F12]/15">
                  Forstå flyskræk
                </span>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-[#123F13] mt-3">
                  Hvad er flyskræk?
                </h2>
              </div>

              <div className="space-y-4 text-base sm:text-lg text-[#3B352F]/85 leading-relaxed">
                <p>
                  Flyskræk er mere end almindelig nervøsitet før en rejse. Den kan vise sig som stærk uro, katastrofetanker, frygt for at miste kontrollen eller en intens opmærksomhed på kroppen, flyets lyde, turbulens og personalets reaktioner.
                </p>
                <p>
                  For nogle begynder frygten først, når de sidder i flyet. For andre starter den dage eller uger før med bekymring, søgning efter information, dårlig søvn eller tanker om at aflyse rejsen.
                </p>
                <p>
                  Du kan godt vide, at flyvning generelt er sikkert, og stadig opleve en stærk alarmreaktion. Derfor handler flyskræk også om, hvordan du reagerer på tanker, kropslige signaler og følelsen af usikkerhed.
                </p>
              </div>

              {/* Symptom list */}
              <div className="bg-[#FAF8F5] p-6 rounded-2xl border border-[#E8DDCE] space-y-4 shadow-2xs">
                <h3 className="text-xl font-bold text-[#123F13] font-serif">
                  Flyskræk kan blandt andet vise sig som
                </h3>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    "Katastrofetanker",
                    "Kropslig uro",
                    "Frygt for panik",
                    "Kontrolbehov",
                    "Undgåelse",
                    "Bekymring før rejsen"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 bg-white p-3.5 rounded-xl border border-[#E8DDCE] shadow-2xs">
                      <CheckCircle2 className="w-5 h-5 text-[#8C2F12] shrink-0" />
                      <span className="font-semibold text-[#3B352F]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Image */}
            <div className="lg:col-span-6 space-y-3">
              <div className="bg-white rounded-2xl border border-[#E8DDCE] shadow-sm overflow-hidden p-2.5 sm:p-3 flex items-center justify-center">
                <img
                  src={IMAGES.foerAfgangForberedelsen}
                  alt="Før afgang forberedelsen – illustration af forberedelse og bekymring"
                  className="w-full h-auto block rounded-xl"
                />
              </div>
              <p className="text-sm text-center text-[#3B352F]/75 italic bg-[#FAF8F5] py-1.5 px-4 rounded-lg border border-[#E8DDCE]/40">
                Før afgang kan forberedelse være hjælpsom. Men den kan også blive til mange timers kontrol, tjek og bekymring.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* 3. FLYSKRÆK KAN BEGYNDE FØR AFGANG */}
      <section className="py-16 md:py-24 bg-[#FAF8F5] border-b border-[#E8DDCE]/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          {/* Header */}
          <div className="max-w-4xl space-y-3">
            <span className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold tracking-wider text-[#8C2F12] uppercase bg-[#FDF6F0] px-3.5 py-1.5 rounded-full border border-[#8C2F12]/15">
              Hele mønsteret
            </span>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-serif font-bold text-[#123F13] sm:whitespace-nowrap">
              Flyskræk kan begynde længe før flyet letter
            </h2>
            <p className="text-base sm:text-lg text-[#3B352F]/85 leading-relaxed">
              For mange fylder flyskræk ikke kun under selve flyvningen. Den kan begynde flere dage eller uger før og fortsætte i tankerne bagefter.
            </p>
          </div>

          {/* Image + Subblock */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center bg-white p-6 md:p-8 rounded-2xl border border-[#E8DDCE] shadow-sm">
            <div className="lg:col-span-6 space-y-3">
              <div className="bg-[#FAF8F5] rounded-xl border border-[#E8DDCE]/60 overflow-hidden p-2.5 sm:p-3 flex items-center justify-center">
                <img
                  src={IMAGES.afgang}
                  alt="Afgang – illustration af afgangsmomentet i lufthavnen"
                  className="w-full h-auto block rounded-lg"
                />
              </div>
              <p className="text-sm text-center text-[#3B352F]/75 italic bg-[#FAF8F5] py-1.5 px-4 rounded-lg border border-[#E8DDCE]/40">
                Afgangen gør rejsen konkret. For nogle stiger alarmen, allerede når lufthavnen kommer til syne.
              </p>
            </div>

            <div className="lg:col-span-6 space-y-4">
              <span className="text-xs font-bold text-[#8C2F12] uppercase tracking-wider bg-[#FDF6F0] px-3 py-1 rounded-md border border-[#8C2F12]/10 inline-block">
                Når rejsen bliver virkelig
              </span>
              <h3 className="text-xl sm:text-2xl font-serif font-bold text-[#123F13]">
                Allerede på vej ind kan opmærksomheden låse sig fast
              </h3>
              <p className="text-base text-[#3B352F]/80 leading-relaxed">
                Nogle begynder at overvåge tid, skilte, kroppen og muligheden for at trække sig. Derfor er flyskræk ofte en proces, som starter før boarding.
              </p>
            </div>
          </div>

          {/* 3 Step Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            
            {/* Card 01 */}
            <div className="bg-white p-6 sm:p-8 rounded-2xl border border-[#E8DDCE] shadow-2xs flex flex-col justify-between h-full space-y-6">
              <div className="space-y-4">
                <div className="w-12 h-12 rounded-xl bg-[#F4EFE6] text-[#123F13] font-serif font-bold text-xl flex items-center justify-center border border-[#E8DDCE]">
                  01
                </div>
                <h3 className="text-xl font-serif font-bold text-[#123F13]">
                  Før flyrejsen
                </h3>
                <ul className="space-y-3 text-sm sm:text-base text-[#3B352F]/85">
                  <li className="flex items-start gap-2.5">
                    <span className="text-[#8C2F12] font-bold text-lg leading-none mt-0.5">•</span>
                    <span>Du gennemgår, hvad der kan gå galt</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="text-[#8C2F12] font-bold text-lg leading-none mt-0.5">•</span>
                    <span>Du tjekker vejr, fly eller turbulens</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="text-[#8C2F12] font-bold text-lg leading-none mt-0.5">•</span>
                    <span>Du overvejer, om du skal aflyse</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="text-[#8C2F12] font-bold text-lg leading-none mt-0.5">•</span>
                    <span>Du prøver at forberede dig på alle muligheder</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Card 02 */}
            <div className="bg-white p-6 sm:p-8 rounded-2xl border border-[#E8DDCE] shadow-2xs flex flex-col justify-between h-full space-y-6">
              <div className="space-y-4">
                <div className="w-12 h-12 rounded-xl bg-[#123F13] text-white font-serif font-bold text-xl flex items-center justify-center shadow-2xs">
                  02
                </div>
                <h3 className="text-xl font-serif font-bold text-[#123F13]">
                  Under flyrejsen
                </h3>
                <ul className="space-y-3 text-sm sm:text-base text-[#3B352F]/85">
                  <li className="flex items-start gap-2.5">
                    <span className="text-[#8C2F12] font-bold text-lg leading-none mt-0.5">•</span>
                    <span>Du holder øje med lyde</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="text-[#8C2F12] font-bold text-lg leading-none mt-0.5">•</span>
                    <span>Du scanner personalets ansigter</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="text-[#8C2F12] font-bold text-lg leading-none mt-0.5">•</span>
                    <span>Du overvåger kroppen</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="text-[#8C2F12] font-bold text-lg leading-none mt-0.5">•</span>
                    <span>Du kæmper mod uro og katastrofetanker</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Card 03 */}
            <div className="bg-white p-6 sm:p-8 rounded-2xl border border-[#E8DDCE] shadow-2xs flex flex-col justify-between h-full space-y-6">
              <div className="space-y-4">
                <div className="w-12 h-12 rounded-xl bg-[#FDF6F0] text-[#8C2F12] border border-[#8C2F12]/20 font-serif font-bold text-xl flex items-center justify-center">
                  03
                </div>
                <h3 className="text-xl font-serif font-bold text-[#123F13]">
                  Efter flyrejsen
                </h3>
                <ul className="space-y-3 text-sm sm:text-base text-[#3B352F]/85">
                  <li className="flex items-start gap-2.5">
                    <span className="text-[#8C2F12] font-bold text-lg leading-none mt-0.5">•</span>
                    <span>Du gennemgår det, der føltes farligt</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="text-[#8C2F12] font-bold text-lg leading-none mt-0.5">•</span>
                    <span>Du analyserer, hvordan du klarede det</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="text-[#8C2F12] font-bold text-lg leading-none mt-0.5">•</span>
                    <span>Du planlægger næste tur</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="text-[#8C2F12] font-bold text-lg leading-none mt-0.5">•</span>
                    <span>Du lover måske dig selv aldrig at flyve igen</span>
                  </li>
                </ul>
              </div>
            </div>

          </div>

          <div className="text-center pt-2">
            <p className="text-base sm:text-lg font-medium text-[#123F13] bg-white inline-block px-6 py-3 rounded-xl border border-[#E8DDCE] shadow-2xs">
              Flyskræk kan derfor fylde meget mere end de timer, du faktisk sidder i flyet.
            </p>
          </div>

        </div>
      </section>

      {/* 4. BILLEDKARUSSEL MED FEM SITUATIONER */}
      <section id="fem-situationer" className="py-16 md:py-24 bg-white border-b border-[#E8DDCE]/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          
          <div className="max-w-3xl space-y-3">
            <span className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold tracking-wider text-[#8C2F12] uppercase bg-[#FDF6F0] px-3.5 py-1.5 rounded-full border border-[#8C2F12]/15">
              Fem situationer på rejsen
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-[#123F13]">
              Flyskræk før, under og efter rejsen
            </h2>
            <p className="text-base sm:text-lg text-[#3B352F]/85 leading-relaxed">
              Karussellen følger den samme person fra bekymringen hjemme til at lade rejsen slutte uden at analysere den til bunds. Brug pilene, prikkerne eller swipe.
            </p>
          </div>

          <Carousel />

        </div>
      </section>

      {/* 5. REJSEN KAN OPDELES I MINDRE SITUATIONER */}
      <section className="py-16 md:py-24 bg-[#FAF8F5] border-b border-[#E8DDCE]/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            
            {/* Image */}
            <div className="lg:col-span-6 order-2 lg:order-1">
              <div className="bg-white rounded-2xl border border-[#E8DDCE] shadow-sm overflow-hidden p-2.5 sm:p-3 flex items-center justify-center">
                <img
                  src={IMAGES.sikkerhedRoligeSkridt}
                  alt="Sikkerhed – rolige skridt. Illustration af lufthavnens sikkerhedskontrol"
                  className="w-full h-auto block rounded-xl"
                />
              </div>
            </div>

            {/* Content */}
            <div className="lg:col-span-6 order-1 lg:order-2 space-y-6">
              <div>
                <span className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold tracking-wider text-[#8C2F12] uppercase bg-[#FDF6F0] px-3.5 py-1.5 rounded-full border border-[#8C2F12]/15">
                  Sikkerhed – rolige skridt
                </span>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-[#123F13] mt-3">
                  Rejsen kan deles op i mindre situationer
                </h2>
              </div>

              <div className="space-y-4 text-base sm:text-lg text-[#3B352F]/85 leading-relaxed">
                <p>
                  I stedet for at skulle håndtere hele flyrejsen på én gang kan behandlingen tage udgangspunkt i de enkelte trin: ankomst, sikkerhedskontrol, gate, boarding, start og tiden i luften.
                </p>
                <p>
                  Sikkerhedskontrollen kan være endnu et konkret trin, hvor du kan øve at tage ét roligt skridt ad gangen.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 6. METAKOGNITIV RETNING */}
      <section className="py-16 md:py-24 bg-white border-b border-[#E8DDCE]/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          <div className="max-w-3xl space-y-3">
            <span className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold tracking-wider text-[#8C2F12] uppercase bg-[#FDF6F0] px-3.5 py-1.5 rounded-full border border-[#8C2F12]/15">
              Metakognitiv retning
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-[#123F13]">
              Du behøver ikke vinde over hver tanke
            </h2>
            <p className="text-base sm:text-lg text-[#3B352F]/85 leading-relaxed">
              Arbejdet handler om at opdage, hvad der holder alarmen aktiv, og træne en mere fleksibel måde at forholde sig til tanker, kropslige signaler og usikkerhed på.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            
            <div className="bg-[#FAF8F5] p-6 rounded-2xl border border-[#E8DDCE] space-y-4 flex flex-col justify-between h-full shadow-2xs hover:border-[#123F13]/30 transition-all">
              <div className="space-y-3">
                <div className="w-10 h-10 rounded-xl bg-[#123F13] text-white flex items-center justify-center font-bold text-base shadow-2xs">
                  1
                </div>
                <h3 className="text-lg font-serif font-bold text-[#123F13] leading-snug">
                  Kortlæg bekymring og overvågning
                </h3>
              </div>
              <p className="text-sm text-[#3B352F]/80 leading-relaxed border-t border-[#E8DDCE]/60 pt-3 mt-2">
                Hvornår begynder du at tjekke, analysere eller forsøge at få sikkerhed?
              </p>
            </div>

            <div className="bg-[#FAF8F5] p-6 rounded-2xl border border-[#E8DDCE] space-y-4 flex flex-col justify-between h-full shadow-2xs hover:border-[#123F13]/30 transition-all">
              <div className="space-y-3">
                <div className="w-10 h-10 rounded-xl bg-[#123F13] text-white flex items-center justify-center font-bold text-base shadow-2xs">
                  2
                </div>
                <h3 className="text-lg font-serif font-bold text-[#123F13] leading-snug">
                  Skab afstand til katastrofetankerne
                </h3>
              </div>
              <p className="text-sm text-[#3B352F]/80 leading-relaxed border-t border-[#E8DDCE]/60 pt-3 mt-2">
                Tanker kan være til stede uden at skulle undersøges eller besvares.
              </p>
            </div>

            <div className="bg-[#FAF8F5] p-6 rounded-2xl border border-[#E8DDCE] space-y-4 flex flex-col justify-between h-full shadow-2xs hover:border-[#123F13]/30 transition-all">
              <div className="space-y-3">
                <div className="w-10 h-10 rounded-xl bg-[#123F13] text-white flex items-center justify-center font-bold text-base shadow-2xs">
                  3
                </div>
                <h3 className="text-lg font-serif font-bold text-[#123F13] leading-snug">
                  Træn fleksibel opmærksomhed
                </h3>
              </div>
              <p className="text-sm text-[#3B352F]/80 leading-relaxed border-t border-[#E8DDCE]/60 pt-3 mt-2">
                Opmærksomheden kan flyttes og udvides, også når kroppen larmer.
              </p>
            </div>

            <div className="bg-[#FAF8F5] p-6 rounded-2xl border border-[#E8DDCE] space-y-4 flex flex-col justify-between h-full shadow-2xs hover:border-[#123F13]/30 transition-all">
              <div className="space-y-3">
                <div className="w-10 h-10 rounded-xl bg-[#123F13] text-white flex items-center justify-center font-bold text-base shadow-2xs">
                  4
                </div>
                <h3 className="text-lg font-serif font-bold text-[#123F13] leading-snug">
                  Øv valg frem for kontrol
                </h3>
              </div>
              <p className="text-sm text-[#3B352F]/80 leading-relaxed border-t border-[#E8DDCE]/60 pt-3 mt-2">
                Målet er større frihed til at rejse – ikke garanti for en helt rolig flyvetur.
              </p>
            </div>

          </div>

        </div>
      </section>

      {/* 7. BOARDING */}
      <section className="py-16 md:py-24 bg-[#FAF8F5] border-b border-[#E8DDCE]/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            
            {/* Content */}
            <div className="lg:col-span-6 space-y-6">
              <div>
                <span className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold tracking-wider text-[#8C2F12] uppercase bg-[#FDF6F0] px-3.5 py-1.5 rounded-full border border-[#8C2F12]/15">
                  Boarding – ombord
                </span>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-[#123F13] mt-3">
                  Du kan gå ombord, selv om tankerne stadig larmer
                </h2>
              </div>

              <div className="space-y-4 text-base sm:text-lg text-[#3B352F]/85 leading-relaxed">
                <p>
                  Målet er ikke at vente på den perfekte følelse af sikkerhed. Målet er større frihed til at fortsætte rejsen, uden at hver katastrofetanke skal analyseres eller besvares.
                </p>
                <p>
                  Boarding er ofte et tydeligt vendepunkt: nu går du videre, også selv om hjernen sender alarmsignaler.
                </p>
              </div>
            </div>

            {/* Image */}
            <div className="lg:col-span-6">
              <div className="bg-white rounded-2xl border border-[#E8DDCE] shadow-sm overflow-hidden p-2.5 sm:p-3 flex items-center justify-center">
                <img
                  src={IMAGES.boardingOmbord}
                  alt="Boarding – ombord. Illustration af boarding og indstigning i flyet"
                  className="w-full h-auto block rounded-xl"
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 8. BEHANDLINGEN */}
      <section className="py-16 md:py-24 bg-white border-b border-[#E8DDCE]/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          <div className="max-w-3xl space-y-3">
            <span className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold tracking-wider text-[#8C2F12] uppercase bg-[#FDF6F0] px-3.5 py-1.5 rounded-full border border-[#8C2F12]/15">
              Behandlingen
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-[#123F13]">
              Metakognitiv terapi og hypnose
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            
            {/* Metakognitiv Terapi */}
            <div className="bg-[#FAF8F5] p-6 sm:p-8 rounded-2xl border border-[#E8DDCE] space-y-6 flex flex-col justify-between h-full shadow-2xs">
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 text-[#123F13] bg-white px-3.5 py-1.5 rounded-lg text-xs sm:text-sm font-bold border border-[#E8DDCE]">
                  <Brain className="w-4 h-4 text-[#123F13]" />
                  <span>Metakognitiv terapi</span>
                </div>
                <h3 className="text-xl sm:text-2xl font-serif font-bold text-[#123F13]">
                  Metakognitiv terapi
                </h3>
                <p className="text-base text-[#3B352F]/85 leading-relaxed">
                  Vi arbejder med bekymring, trusselsmonitorering, kontrolforsøg og den tid, der bruges på at analysere flyrejsen før, under og efter.
                </p>
                <ul className="space-y-3 pt-2">
                  {[
                    "Afkoblet opmærksomhed",
                    "Fleksibel opmærksomhed",
                    "Mindre overvågning af krop og fly"
                  ].map((bullet, idx) => (
                    <li key={idx} className="flex items-center gap-3 bg-white p-3 rounded-xl border border-[#E8DDCE]/70">
                      <CheckCircle2 className="w-5 h-5 text-[#123F13] shrink-0" />
                      <span className="font-semibold text-[#3B352F]">{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Hypnose mod flyskræk – som supplement */}
            <div className="bg-[#FAF8F5] p-6 sm:p-8 rounded-2xl border border-[#E8DDCE] space-y-6 flex flex-col justify-between h-full shadow-2xs">
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 text-[#8C2F12] bg-[#FDF6F0] px-3.5 py-1.5 rounded-lg text-xs sm:text-sm font-bold border border-[#8C2F12]/15">
                  <Sparkles className="w-4 h-4 text-[#8C2F12]" />
                  <span>Supplerende mulighed</span>
                </div>
                <h3 className="text-xl sm:text-2xl font-serif font-bold text-[#123F13]">
                  Hypnose mod flyskræk – som supplement
                </h3>
                <div className="space-y-4 text-base text-[#3B352F]/85 leading-relaxed">
                  <p>
                    I mit arbejde med flyskræk kombinerer jeg metakognitiv terapi med hypnose, når det giver mening for den enkelte. Min tilgang er ikke at forsøge at fjerne angsten, men at hjælpe dig med at forholde dig anderledes til de tanker og kropslige reaktioner, der kan opstå, når du skal flyve.
                  </p>
                  <p>
                    Jeg bruger hypnose som en rolig og fokuseret måde at arbejde med konkrete situationer på. Det kan være ventetiden ved gaten, boarding, flyets start eller turbulens. Her arbejder vi med opmærksomhed, forestillingsbilleder og nye reaktionsmuligheder.
                  </p>
                  <p>
                    Det, jeg lægger særlig vægt på, er, at du ikke behøver føle dig fuldstændig rolig for at kunne gennemføre en flyrejse. Målet er at udvikle større fleksibilitet i måden, du møder uroen på – ikke at love, at den forsvinder.
                  </p>
                </div>
                <ul className="space-y-3 pt-2">
                  {[
                    "Rolig guidning",
                    "Ressourceaktivering",
                    "Træning af nye reaktionsmuligheder"
                  ].map((bullet, idx) => (
                    <li key={idx} className="flex items-center gap-3 bg-white p-3 rounded-xl border border-[#E8DDCE]/70">
                      <CheckCircle2 className="w-5 h-5 text-[#8C2F12] shrink-0" />
                      <span className="font-semibold text-[#3B352F]">{bullet}</span>
                    </li>
                  ))}
                </ul>
                <div className="pt-2">
                  <Link
                    to="/hypnose"
                    className="inline-flex items-center gap-2 font-semibold text-[#8C2F12] hover:text-[#70250e] hover:underline transition-colors"
                  >
                    <span>Læs mere om hypnose</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>

          </div>

          <div className="p-4 bg-[#F4EFE6] rounded-xl border border-[#E8DDCE] text-center text-sm sm:text-base font-medium text-[#123F13]">
            Bemærk: Hypnose er en mulighed og ikke et krav, og behandlingen kan også gennemføres fuldstændigt uden hypnose.
          </div>

        </div>
      </section>

      {/* 9. TILBUDSSEKTION */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-[#FAF8F5] to-[#F4EFE6] border-b border-[#E8DDCE]/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl p-6 sm:p-8 md:p-12 border border-[#E8DDCE] shadow-lg">
            
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              
              {/* Offer details & text */}
              <div className="lg:col-span-7 space-y-6">
                <div>
                  <span className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold tracking-wider text-[#8C2F12] uppercase bg-[#FDF6F0] px-3.5 py-1.5 rounded-full border border-[#8C2F12]/15">
                    Første behandlingssession
                  </span>
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-[#123F13] mt-3">
                    Et konkret første skridt
                  </h2>
                </div>

                <p className="text-base sm:text-lg text-[#3B352F]/85 leading-relaxed">
                  Ved første session kortlægger vi din flyskræk og begynder behandlingen. Bagefter får du en skriftlig behandlingsplan.
                </p>

                {/* Info badges grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-2">
                  <div className="flex items-center gap-3.5 bg-[#FAF8F5] p-3.5 sm:p-4 rounded-2xl border border-[#E8DDCE]">
                    <div className="w-10 h-10 rounded-xl bg-white border border-[#E8DDCE] flex items-center justify-center shrink-0 shadow-2xs">
                      <Tag className="w-5 h-5 text-[#8C2F12]" />
                    </div>
                    <div>
                      <div className="text-xs text-[#3B352F]/70 font-medium">Introduktionspris</div>
                      <div className="text-lg font-bold text-[#123F13]">375 kr.</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3.5 bg-[#FAF8F5] p-3.5 sm:p-4 rounded-2xl border border-[#E8DDCE]">
                    <div className="w-10 h-10 rounded-xl bg-white border border-[#E8DDCE] flex items-center justify-center shrink-0 shadow-2xs">
                      <Clock className="w-5 h-5 text-[#123F13]" />
                    </div>
                    <div>
                      <div className="text-xs text-[#3B352F]/70 font-medium">Varighed</div>
                      <div className="text-base font-bold text-[#123F13]">50 minutter</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3.5 bg-[#FAF8F5] p-3.5 sm:p-4 rounded-2xl border border-[#E8DDCE]">
                    <div className="w-10 h-10 rounded-xl bg-white border border-[#E8DDCE] flex items-center justify-center shrink-0 shadow-2xs">
                      <ShieldCheck className="w-5 h-5 text-[#8C2F12]" />
                    </div>
                    <div>
                      <div className="text-xs text-[#3B352F]/70 font-medium">Målgruppe</div>
                      <div className="text-base font-bold text-[#123F13]">Kun for nye klienter</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3.5 bg-[#FAF8F5] p-3.5 sm:p-4 rounded-2xl border border-[#E8DDCE]">
                    <div className="w-10 h-10 rounded-xl bg-white border border-[#E8DDCE] flex items-center justify-center shrink-0 shadow-2xs">
                      <MapPin className="w-5 h-5 text-[#123F13]" />
                    </div>
                    <div>
                      <div className="text-xs text-[#3B352F]/70 font-medium">Placering</div>
                      <div className="text-base font-bold text-[#123F13]">Valby eller online</div>
                    </div>
                  </div>
                </div>

                {/* Additional clear conditions */}
                <ul className="space-y-2.5 text-sm text-[#3B352F]/85 pt-1">
                  <li className="flex items-center gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-[#123F13] shrink-0" />
                    <span>Ingen binding til et behandlingsforløb</span>
                  </li>
                  <li className="flex items-center gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-[#123F13] shrink-0" />
                    <span>Afklaring og behandling i samme session</span>
                  </li>
                </ul>

                {/* Primary CTA */}
                <div className="pt-2">
                  <Link
                    to="/bestil-tid"
                    className="inline-flex items-center justify-center gap-3 bg-[#123F13] text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-[#123F13]/90 transition-all shadow-md hover:shadow-lg active:scale-[0.99] w-full sm:w-auto text-center"
                  >
                    <Calendar className="w-5 h-5" />
                    <span>Book første behandlingssession</span>
                  </Link>
                </div>
              </div>

              {/* Therapist Portrait */}
              <div className="lg:col-span-5 space-y-3">
                <div className="bg-[#FAF8F5] rounded-2xl border border-[#E8DDCE] shadow-sm overflow-hidden p-2 sm:p-2.5">
                  <div className="w-full overflow-hidden flex items-center justify-center">
                    <img
                      src={IMAGES.larsPortraet}
                      alt="Lars Nissen Corell – Psykoterapeut MPF"
                      className="w-full h-auto block rounded-xl"
                    />
                  </div>
                  <div className="p-3 text-center bg-white border-t border-[#E8DDCE] rounded-b-xl mt-2">
                    <div className="font-bold text-[#123F13] font-serif text-lg">Lars Nissen Corell – Psykoterapeut MPF</div>
                    <div className="text-xs text-[#3B352F]/75">Certificeret metakognitiv psykoterapeut (Level IV), supervisor & hypnoterapeut</div>
                  </div>
                </div>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* 10. FAQ */}
      <section className="flight-faq-section">
        <div className="flight-faq-head">
          <span className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold tracking-wider text-[#8C2F12] uppercase bg-white px-3.5 py-1.5 rounded-full border border-[#8C2F12]/15 mb-3 shadow-2xs">
            Spørgsmål og svar
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-[#123F13] mb-3">
            Ofte stillede spørgsmål om flyskræk
          </h2>
          <p className="text-base sm:text-lg text-[#3B352F]/80 max-w-2xl mx-auto">
            Her finder du korte svar på nogle af de spørgsmål, der ofte fylder før behandling.
          </p>
        </div>

        <div className="flight-faq-list">
          {FLIGHT_FAQ_ITEMS.map((item, index) => (
            <details
              key={index}
              className="flight-faq-item"
              open={index === 0}
            >
              <summary>{item.question}</summary>
              <div className="flight-faq-body">
                <div className="flight-faq-visual">
                  <div className="flight-faq-answer">
                    <span className="flight-faq-badge-title">{item.answerBadge.smallText}</span>
                    <div className="flight-faq-badge-text">{item.answerBadge.bigText}</div>
                  </div>

                  <div className="flight-faq-grid">
                    {item.cards.map((card, cIdx) => (
                      <div key={cIdx} className="flight-faq-card">
                        <div className="flight-faq-icon">
                          {card.icon}
                        </div>
                        <div className="flight-faq-rule" />
                        <h4>{card.title}</h4>
                        <p>{card.text}</p>
                      </div>
                    ))}
                  </div>

                  <div className="flight-faq-summary">
                    <div className="flight-faq-summary-icon">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>{item.summary}</div>
                  </div>

                  <div className="flight-faq-links">
                    {item.links.map((lnk, lIdx) => (
                      <Link key={lIdx} to={lnk.path}>
                        <span>{lnk.label}</span>
                        <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                        </svg>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </details>
          ))}
        </div>
      </section>

      {/* Relevante sider */}
      <section className="py-20 bg-white border-t border-[#E8DDCE]/80">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12 space-y-4">
            <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-2">
              <span className="w-8 h-[1px] bg-[#8C2F12]"></span>
              Læs også
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] leading-tight font-semibold">
              Relevante sider
            </h2>
            <p className="text-[#3B352F]/70 leading-relaxed">
              Her kan du læse mere om områder, der ofte hænger sammen med flyskræk, angst og kropslig uro.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Angst", path: "/angst" },
              { label: "Panikangst", path: "/panikangst" },
              { label: "Generaliseret angst", path: "/generaliseret-angst" },
              { label: "Tankemylder", path: "/tankemylder" },
              { label: "Metakognitiv terapi", path: "/metakognitiv-terapi" },
              { label: "Hypnose", path: "/hypnose" },
              { label: "Eksamensangst & præstationsangst", path: "/eksamensangst-praestationsangst" },
              { label: "Online sessioner", path: "/online-sessioner" }
            ].map((item, idx) => (
              <Link 
                key={idx} 
                to={item.path} 
                className="flex items-center justify-between p-4 bg-white border border-[#D2C5B3]/95 rounded-xl text-sm font-bold text-[#123F13] hover:border-[#CBB9A1] hover:shadow-sm hover:-translate-y-0.5 transition-all"
              >
                <span>{item.label}</span>
                <span className="text-[#8C2F12] font-bold">›</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 11. AFSLUTTENDE CTA */}
      <section className="py-16 md:py-20 bg-[#F4EFE6]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <div>
            <span className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold tracking-wider text-[#8C2F12] uppercase bg-white px-3.5 py-1.5 rounded-full border border-[#8C2F12]/15 shadow-2xs">
              Næste skridt
            </span>
          </div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-[#123F13] max-w-2xl mx-auto">
            Flyskræk behøver ikke bestemme, hvor du kan tage hen
          </h2>

          <p className="text-base sm:text-lg text-[#3B352F]/85 max-w-xl mx-auto">
            Book en første behandlingssession i Valby eller online.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
            <Link
              to="/bestil-tid"
              className="inline-flex items-center justify-center gap-2 bg-[#123F13] text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-[#123F13]/90 transition-all shadow-md hover:shadow-lg active:scale-[0.99] w-full sm:w-auto text-center"
            >
              <Calendar className="w-5 h-5" />
              <span>Book første behandlingssession</span>
            </Link>

            <div className="bg-white border border-[#E8DDCE] px-5 py-3.5 rounded-xl text-sm font-bold text-[#123F13] shrink-0 shadow-2xs">
              375 kr. (50 min.)
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
